import type { SessionAttendeeAttributes, SessionRegistrationAttributes } from '@/server/models'
import type { SessionBreakoutAttributes } from '@/server/models/SessionBreakout'

import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import SessionQ from './SessionQ'

const SessionBreakoutQ = {
  ...makeQueryModelV2<SessionBreakoutAttributes>('session_breakout')([
    'id',
    'session_id',
    'zoom_meeting_id',
    'zoom_start_date',
    'zoom_end_date',
    'zoom_recording_urls',
    'title',
    'safety_summary_automated',
    'safety_risk_automated',
    'transcript_url',
    'safety_check_by_user_id',
    'safety_check_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: SessionBreakoutQ.session_id,
    },
    SessionRegistrations: {
      query: SessionRegistrationQ,
      foreignKey: SessionRegistrationQ.session_breakout_id,
      sourceKey: SessionBreakoutQ.id,
    },
    SessionAttendees: {
      query: SessionAttendeeQ,
      foreignKey: SessionAttendeeQ.session_breakout_id,
      sourceKey: SessionBreakoutQ.id,
    },
  }),

  where: (
    {
      id,
      sessionId,
      paranoid = true,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      paranoid?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionBreakoutAttributes> => SQL`
      FROM ${SessionBreakoutQ}
      ${includeAssociationsToSql(SessionBreakoutQ, include)}
      WHERE
        ${id != null ? SQL`${SessionBreakoutQ.id} = ${id} AND` : SQL``}
        ${sessionId != null ? SQL`${SessionBreakoutQ.session_id} = ${sessionId} AND` : SQL``}
        ${paranoid ? SQL`${SessionBreakoutQ.deleted_at} IS NULL AND` : SQL``}
        ${customWhere != null ? customWhere : SQL`TRUE`}
      ${queryGroupByToSQL(groupBy)}
      ${queryOrderToSQL(order)}
      ${queryLimitToSQL(limit)}
  `,

  getRegistrations: (): SQLFragment<SessionRegistrationAttributes> =>
    SessionRegistrationQ.where({ sessionBreakoutId: SessionBreakoutQ.id }),

  getAttendees: (): SQLFragment<SessionAttendeeAttributes> =>
    SessionAttendeeQ.where({ sessionBreakoutId: SessionBreakoutQ.id }),

  isOneOnOne: (sessionBreakoutId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`(
    SELECT COUNT(DISTINCT ${SessionAttendeeQ.user_id}) = 2
    ${SessionAttendeeQ.where({ sessionBreakoutId })}
    )
  `,
}

export default SessionBreakoutQ
